class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.installScrollGuard();
  }

  installScrollGuard() {
    let lastY = 0;
    this.addEventListener('touchstart', (event) => {
      if (event.touches.length === 1) lastY = event.touches[0].clientY;
    }, { passive: true });
    this.addEventListener('touchmove', (event) => {
      if (!this.classList.contains('active') || event.touches.length !== 1) return;
      const target = event.target;
      if (target.closest('textarea, input, select')) return;
      const y = event.touches[0].clientY;
      const delta = lastY - y;
      lastY = y;
      const inner = this.querySelector('.drawer__inner');
      const items = this.classList.contains('is-empty') || inner.scrollHeight > inner.clientHeight + 1 ? inner : this.querySelector('cart-drawer-items');
      if (!items || !target.closest('.drawer__inner')) {
        if (event.cancelable) event.preventDefault();
        return;
      }
      const insideItems = items.contains(target);
      const atEdge = (delta < 0 && items.scrollTop <= 0) ||
        (delta > 0 && items.scrollTop + items.clientHeight >= items.scrollHeight - 1);
      if (!insideItems || atEdge) {
        if (event.cancelable) event.preventDefault();
        if (!insideItems) items.scrollTop += delta;
      }
    }, { passive: false });
    this.addEventListener('wheel', (event) => {
      if (!this.classList.contains('active')) return;
      const inner = this.querySelector('.drawer__inner');
      const items = this.classList.contains('is-empty') || inner.scrollHeight > inner.clientHeight + 1 ? inner : this.querySelector('cart-drawer-items');
      if (items && event.target.closest('.drawer__inner') && !items.contains(event.target)) {
        event.preventDefault();
        items.scrollTop += event.deltaY;
      }
    }, { passive: false });
  }

  lockPageScroll() {
    if (this.pageScrollLock) return;
    const body = document.body;
    const properties = ['position', 'top', 'left', 'right', 'width', 'overflow'];
    this.pageScrollLock = {
      y: window.scrollY,
      x: window.scrollX,
      styles: properties.map((key) => [key, body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]),
    };
    body.style.setProperty('position', 'fixed');
    body.style.setProperty('top', `-${this.pageScrollLock.y}px`);
    body.style.setProperty('left', '0');
    body.style.setProperty('right', '0');
    body.style.setProperty('width', '100%');
    body.style.setProperty('overflow', 'hidden');
  }

  unlockPageScroll() {
    if (!this.pageScrollLock) return;
    const saved = this.pageScrollLock;
    this.pageScrollLock = null;
    saved.styles.forEach(([key, value, priority]) => {
      if (value) document.body.style.setProperty(key, value, priority);
      else document.body.style.removeProperty(key);
    });
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.lockPageScroll();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.unlockPageScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('sn-cart-quick-add')) {
  customElements.define('sn-cart-quick-add', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelector('form').addEventListener('submit', this.add.bind(this));
    }

    async add(event) {
      event.preventDefault();
      const drawer = this.closest('cart-drawer');
      if (drawer.dataset.quickAddPending === 'true') return;
      drawer.dataset.quickAddPending = 'true';
      const buttons = drawer.querySelectorAll('sn-cart-quick-add button');
      buttons.forEach((button) => { button.disabled = true; });
      const button = this.querySelector('button');
      const error = this.querySelector('[role="alert"]');
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;
      try {
        const form = event.currentTarget;
        const data = new FormData(form);
        const mainCartFooter = document.getElementById('main-cart-footer');
        const footerSection = mainCartFooter?.dataset.id;
        const sectionIds = drawer.getSectionsToRender().map((section) => section.id);
        if (footerSection) sectionIds.push(footerSection);
        data.append('sections', sectionIds.join(','));
        data.append('sections_url', window.location.pathname);
        const response = await fetch(form.action, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          error.textContent = typeof result.description === 'string' ? result.description : this.dataset.error;
          error.hidden = false;
          return;
        }
        drawer.setActiveElement(document.querySelector('#cart-icon-bubble'));
        if (!result.sections || !result.sections['cart-drawer']) {
          window.location.assign(window.routes.cart_url);
          return;
        }
        drawer.classList.remove('is-empty');
        drawer.renderContents(result);
        if (footerSection && result.sections[footerSection]) {
          const footerHTML = new DOMParser().parseFromString(result.sections[footerSection], 'text/html');
          const footerContents = footerHTML.querySelector('.js-contents');
          const currentContents = mainCartFooter.querySelector('.js-contents');
          if (footerContents && currentContents) currentContents.innerHTML = footerContents.innerHTML;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sn-cart-quick-add', productVariantId: Number(data.get('id')), cartData: result });
        }
        setTimeout(() => {
          const inner = drawer.querySelector('.drawer__inner');
          if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
            trapFocus(inner, inner.querySelector('.drawer__close'));
          }
        }, 0);
      } catch (failure) {
        error.textContent = this.dataset.error;
        error.hidden = false;
      } finally {
        delete drawer.dataset.quickAddPending;
        buttons.forEach((control) => { control.disabled = false; });
        button.removeAttribute('aria-busy');
      }
    }
  });
}

